import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { useMenuContext } from '../contexts/MenuContext';
import { BREAK_POINTS } from '../constants';

import { 
  MENU_ICON, CLOSE_ICON,
  FACEBOOK_ICON, INSTAGRAM_ICON, YOUTUBE_ICON, X_ICON
} from '../icons';
import { LABELS } from '../images';

const MenuWrapper = styled.div`
  .hamburger {
    font-size: 24px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 10px;
    display: flex;
    align-items: center;

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      padding: 8px;
    }
  }

  .sidebar {
    position: fixed;
    top: 0;
    right: 0;
    width: 590px;
    height: 100vh;
    background-color: #fff;
    box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
    padding: 79.5px;
    display: flex;
    flex-direction: column;

    @media (max-width: ${BREAK_POINTS.TABLET}px) {
      width: 480px;
      padding: 60px;
    }

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      width: 100%;
      padding: 40px;
    }
  }

  .close-btn {
    position: absolute;
    top:128px;
    right:210px;
    font-size: 24px;
    background: none;
    border: none;
    cursor: pointer;

    @media (max-width: ${BREAK_POINTS.TABLET}px) {
      top: 100px;
      right: 160px;
    }

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      top: 80px;
      right: 40px;
      font-size: 20px;
    }
  }

  .sidebar ul {
    margin-top: 100px;

    @media (max-width: ${BREAK_POINTS.TABLET}px) {
      margin-top: 80px;
    }

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      margin-top: 60px;
    }
  }

  .sidebar li {
    margin: 48px 0;

    @media (max-width: ${BREAK_POINTS.TABLET}px) {
      margin: 40px 0;
    }

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      margin: 32px 0;
    }
  }

  .sidebar a {
    text-decoration: none;
    font-size: 24px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: -0.08px;
    color: #505050;

    @media (max-width: ${BREAK_POINTS.TABLET}px) {
      font-size: 22px;
    }

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      font-size: 20px;
    }
  }

  .sidebar a:hover {
    font-weight: bold;
    color: #7172a5;
    transition: color 1s ease;
  }
`;

const SocialIcon = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 96px;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    margin-top: 80px;
    gap: 16px;
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    margin-top: 60px;
    gap: 12px;
  }

  a {
    display: flex;
    align-items: center;
  }

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;

    @media (max-width: ${BREAK_POINTS.MOBILE}px) {
      width: 20px;
      height: 20px;
    }
  }
`;

const Icon = styled.img`
  width: 32px;
  height: 32px;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    width: 28px;
    height: 28px;
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    width: 24px;
    height: 24px;
  }
`;

const LabelLogos = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 96px;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    margin-top: 80px;
    gap: 20px;
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    margin-top: 60px;
    gap: 16px;
  }
`;

const Label = styled.img`
  width: 158px;
  height: 60px;

  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const Menu = () => {
  const { isOpen, setIsOpen } = useMenuContext();

  const socialLinks = [
    { icon: INSTAGRAM_ICON, url: 'https://www.instagram.com/kmg__official/#', alt: 'instagram' },
    { icon: YOUTUBE_ICON, url: 'https://www.youtube.com/channel/UC7hoeMGKttVmc9qtbk76i0g', alt: 'youtube' },
    { icon: FACEBOOK_ICON, url: 'https://www.facebook.com/kmgactors', alt: 'facebook' },
    { icon: X_ICON, url: 'https://x.com/kmg__official', alt: 'x (twitter)' }
  ];

  const labelLinks = [
    { icon: LABELS.RNC, url: 'https://www.instagram.com/rnc_ent_official', alt: 'RNC' },
    { icon: LABELS.HIGHBRID, url: 'https://highbrid.co.kr', alt: 'Highbrid' },
    { icon: LABELS.HANDMADE, url: 'http://www.hdmd.co.kr/', alt: 'Handmade' }
  ];

  return (
    <MenuWrapper>
      <button className="hamburger" onClick={() => setIsOpen(!isOpen)}>
        <Icon src={MENU_ICON} alt="menu" />
      </button>

      <motion.div
        className="sidebar"
        initial={{ x: "100%" }}
        animate={{ x: isOpen ? 0 : "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <button className="close-btn" onClick={() => setIsOpen(false)}>
          <Icon src={CLOSE_ICON} alt="close" />
        </button>
        <ul>
          <li><Link to="/artist" onClick={() => setIsOpen(false)}>ARTISTS</Link></li>
          {/* <li><Link to="/press/page/1" onClick={() => setIsOpen(false)}>PRESS</Link></li> */}
          <li><Link to="/company" onClick={() => setIsOpen(false)}>COMPANY</Link></li>
          <li><Link to="/contact" onClick={() => setIsOpen(false)}>CONTACT</Link></li>
        </ul>

        <SocialIcon>
          {socialLinks.map(({ icon, url, alt }) => (
            <a href={url} target="_blank" rel="noreferrer" key={alt}>
              <img src={icon} alt={alt} />
            </a>
          ))}
        </SocialIcon>

        <LabelLogos>
          {labelLinks.map(({ icon, url, alt }) => (
            <a href={url} target="_blank" rel="noreferrer" key={alt}>
              <Label src={icon} alt={alt} />
            </a>
          ))}
        </LabelLogos>
      </motion.div>
    </MenuWrapper>
  );
};

export default Menu;
